import {
  createEndpoint,
  ITEMS_PER_PAGE,
  useRemoteList,
  useTranslator,
} from 'core';
import React, { ComponentType, memo } from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Input,
  List,
  Menu,
  Table,
} from 'semantic-ui-react';
import { Currency, DataTable, FormattedDate, Page } from 'shared/components';
import { useListFilter } from 'shared/hooks';

interface LendingFilter {
  customer?: string;
  article?: string;
  startDate?: string;
  endDate?: string;
  tags?: string;
  lendingStore?: number;
}

const LendingDataTable: ComponentType = () => {
  const _ = useTranslator();
  const tags = useRemoteList(createEndpoint<API.Tag>('tags'));
  const stores = useRemoteList(createEndpoint<API.Store>('stores'));

  const [filter, { list, query }] = useListFilter<
    LendingFilter & { take?: number; orderBy?: string; order?: string },
    API.Lending
  >(
    ({ onChange, values }) => (
      <>
        <Menu.Item>
          <Input
            placeholder="Kundin"
            onChange={(e) => onChange('customer', e.target.value)}
            value={values.customer ? values.customer : ''}
          />
        </Menu.Item>
        <Menu.Item>
          <Input
            placeholder="Artikel"
            onChange={(e) => onChange('article', e.target.value)}
            value={values.article ? values.article : ''}
          />
        </Menu.Item>
        <List.Item>
          <Input
            size="mini"
            type="date"
            placeholder="Von"
            onChange={(e) => onChange('startDate', e.target.value)}
            value={values.startDate ? values.startDate : ''}
          />
          <br />
          <Input
            size="mini"
            type="date"
            placeholder="Bis"
            onChange={(e) => onChange('endDate', e.target.value)}
            value={values.endDate ? values.endDate : ''}
          />
        </List.Item>
        <Menu.Item>
          <Dropdown
            value={values.tags ? values.tags.split(',').map(Number) : []}
            clearable
            multiple
            search
            placeholder="Tags"
            selection
            onChange={(e: any, { value }) => {
              onChange('tags', (value as number[]).join(','));
            }}
            options={(tags.data || []).map((t) => ({
              text: t.name,
              value: t.id,
            }))}
          />
        </Menu.Item>
        <Menu.Item>
          <Dropdown
            value={values.lendingStore}
            clearable
            placeholder="Store verliehen"
            selection
            onChange={(e: any, { value }) => {
              onChange('lendingStore', value || undefined);
            }}
            options={(stores.data || []).map((s) => ({
              text: s.name,
              value: s.id,
            }))}
          />
        </Menu.Item>
      </>
    ),
    {
      endpointUrl: 'admin/lendings',
      initialState: {
        take: ITEMS_PER_PAGE,
        orderBy: 'lending.lendAt',
        order: 'DESC',
      },
    }
  );

  return (
    <Page header={_('menu.lendings')} loading={list.loading}>
      {filter}
      <Menu>
        <Menu.Item>
          <Button
            icon
            as="a"
            download="export.csv"
            target="_blank"
            href={`${
              process.env.BACKEND_URL
            }/admin/lendings/export?${Object.keys(query)
              .filter((k) => query[k] !== undefined)
              .map(
                (k) =>
                  `${encodeURIComponent(k)}=${encodeURIComponent(query[k])}`
              )
              .join('&')}`}
          >
            <Icon name="file" />
            CSV Export
          </Button>
        </Menu.Item>
      </Menu>
      <DataTable<API.Lending>
        list={list}
        query={query}
        rowComponent={({ data, ...props }) => (
          <Table.Row {...props}>
            <Table.Cell>
              {data.article.name} (#{data.article.id})
            </Table.Cell>
            <Table.Cell>
              <Currency value={data.article.price} />
            </Table.Cell>
            <Table.Cell>{data.customer.name}</Table.Cell>
            <Table.Cell>
              <FormattedDate value={data.lendAt} />
            </Table.Cell>
            <Table.Cell>
              {data.returnedAt ? (
                <FormattedDate value={data.returnedAt} />
              ) : (
                'offen'
              )}
            </Table.Cell>
            <Table.Cell>
              {(data.article.tags || []).map((t) => t.name).join(', ')}
            </Table.Cell>
            <Table.Cell>{data.article.ownerStore?.name}</Table.Cell>
            <Table.Cell>{data.lendingStore}</Table.Cell>
            <Table.Cell>{data.returnedAt ? data.returnStore : ''}</Table.Cell>
          </Table.Row>
        )}
      >
        {(sorting: any) => (
          <Table.Row>
            <Table.HeaderCell
              content="Artikel"
              sorted={sorting.isSorted('article.name')}
              onClick={() => sorting.onSortChange('article.name')}
            />
            <Table.HeaderCell
              content="Preis"
              sorted={sorting.isSorted('article.price')}
              onClick={() => sorting.onSortChange('article.price')}
            />
            <Table.HeaderCell
              content="Kundin"
              sorted={sorting.isSorted('customer.lastName')}
              onClick={() => sorting.onSortChange('customer.lastName')}
            />
            <Table.HeaderCell
              content="Startdatum"
              sorted={sorting.isSorted('lending.lendAt')}
              onClick={() => sorting.onSortChange('lending.lendAt')}
            />
            <Table.HeaderCell
              content="Enddatum"
              sorted={sorting.isSorted('lending.returnedAt')}
              onClick={() => sorting.onSortChange('lending.returnedAt')}
            />
            <Table.HeaderCell content="Tags" />
            <Table.HeaderCell
              content="Besitzer"
              sorted={sorting.isSorted('ownerStore.name')}
              onClick={() => sorting.onSortChange('ownerStore.name')}
            />
            <Table.HeaderCell
              content="Store verliehen"
              sorted={sorting.isSorted('lendingStore.name')}
              onClick={() => sorting.onSortChange('lendingStore.name')}
            />
            <Table.HeaderCell
              content="Store zurückgegeben"
              sorted={sorting.isSorted('returnStore.name')}
              onClick={() => sorting.onSortChange('returnStore.name')}
            />
          </Table.Row>
        )}
      </DataTable>
    </Page>
  );
};

export default memo(LendingDataTable);
